import { useEffect, useState, type ReactNode } from 'react';
import { ArrowLeft, Download, Pencil, ShieldCheck, Users, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useStore } from '../store';
import { exportAnalyticsCsv } from '../exportCsv';
import { bandByKey, formatDate } from '../theme/bands';
import { Radar, type RadarAxis } from '../components/Radar';
import { Count } from '../components/CountUp';
import { Button, Eyebrow, BandChip, PageLoader } from '../components/ui';
import { CRITERION_LABELS, type AdminAnalytics, type BudgetDto, type CampaignMeta, type OrgSummary } from '../types';
import { CustomDimensionsEditor } from './CustomDimensionsEditor';
import { sampleAdmin, sampleBudget, sampleOrganisations } from '../sample';
import { api, isSampleMode } from '../api/client';
import { isAuthed } from '../auth/cognito';
import { aud, audStr } from '../money';

const isLive = () => !isSampleMode && isAuthed();

// Breakdown bar/dot colours by line key (falls back to a palette by index).
const BREAKDOWN_COLOR: Record<string, string> = { conversation: '#00D700', voice: '#2438E6', report: '#6E7180', practice: '#3a3a44' };
const BREAKDOWN_PALETTE = ['#00D700', '#2438E6', '#6E7180', '#3a3a44'];

const STATUS: Record<string, { label: string; ink: string; dot: string }> = {
  active: { label: 'Active', ink: '#00A800', dot: '#00D700' },
  closed: { label: 'Closed', ink: '#75757F', dot: '#9A9AA4' },
  draft: { label: 'Draft', ink: '#C2A500', dot: '#E0C200' },
  archived: { label: 'Archived', ink: '#75757F', dot: '#9A9AA4' },
};

function fmtRange(periodStart?: string, periodEnd?: string): string | undefined {
  if (!periodStart && !periodEnd) return undefined;
  return `${formatDate(periodStart) || '…'} – ${formatDate(periodEnd) || '…'}`;
}

export function OrgDetail() {
  const setScreen = useStore((s) => s.setScreen);
  const selectedOrg = useStore((s) => s.selectedOrg);
  const suppressed = useStore((s) => s.suppressed);
  const toggleSuppressed = useStore((s) => s.toggleSuppressed);
  const identified = useStore((s) => s.identified);
  const toggleIdentified = useStore((s) => s.toggleIdentified);
  const openCampaignModal = useStore((s) => s.openCampaignModal);

  const live = isLive();
  const sampleOrg: OrgSummary | undefined = !live
    ? sampleOrganisations.find((o) => o.campaignId === selectedOrg) ?? sampleOrganisations[0]
    : undefined;

  const [data, setData] = useState<AdminAnalytics | null>(live ? null : sampleAdmin);
  const [budget, setBudget] = useState<BudgetDto | null>(live ? null : sampleBudget);
  const [campaign, setCampaign] = useState<CampaignMeta | undefined>(undefined);
  const [loading, setLoading] = useState(live);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!live) {
      setData(sampleAdmin);
      setBudget(sampleBudget);
      return;
    }
    if (!selectedOrg) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([api.getCampaign(selectedOrg, !suppressed, identified), api.getBudget(selectedOrg)])
      .then(([{ analytics, campaign: meta }, { budget }]) => {
        if (cancelled) return;
        setData(analytics);
        setCampaign(meta);
        setBudget(budget);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(/403/.test(e.message) ? 'Programme admin access required.' : 'Could not load organisation dashboard.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [live, selectedOrg, suppressed, identified]);

  if (loading) return <PageLoader label="Loading organisation dashboard…" />;
  if (error || !data || !budget) return <Message title={error ?? 'No dashboard data available.'} tone="error" onBack={() => setScreen('organisations')} />;

  const a = data;
  const b = budget;

  // Header fields (live: campaign meta + analytics; sample: the selected org row).
  const name = live ? campaign?.name ?? a.campaignId : sampleOrg?.name ?? 'Organisation';
  const sector = live ? campaign?.sector : sampleOrg?.sector;
  const statusKey = (live ? campaign?.status : sampleOrg?.status) ?? 'active';
  const st = STATUS[statusKey] ?? STATUS.active;
  const range = live ? fmtRange(campaign?.periodStart, campaign?.periodEnd) : fmtRange(sampleOrg?.periodStart, sampleOrg?.periodEnd);

  // Derived spend (AUD display; underlying USD).
  const avgUsd = a.kpis.completeCount ? b.spentUsd / a.kpis.completeCount : 0;
  const budgetUsd = b.budgetUsd;
  const budgetPct = budgetUsd && budgetUsd > 0 ? Math.min(100, (b.spentUsd / budgetUsd) * 100) : 0;
  const spentPctOfBudget = budgetUsd && budgetUsd > 0 ? (b.spentUsd / budgetUsd) * 100 : 0;
  const projectedPctOfBudget = budgetUsd && budgetUsd > 0 ? (b.projectedRemainingUsd / budgetUsd) * 100 : 0;

  const radarAxes: RadarAxis[] = a.capabilityRadar.map((c) => ({ criterion: c.criterion, band: c.band.band, level: bandByKey(c.band.band).level }));
  const subLine = [range, `${a.kpis.participantCount} participants`, `${audStr(b.spentUsd)} of ${budgetUsd != null ? audStr(budgetUsd) : '—'} spent`]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="bg-subtle text-code" style={{ flex: 1 }}>
      <div className="fui-admin" style={{ maxWidth: 1180, margin: '0 auto', padding: '34px 32px 80px', fontVariantNumeric: 'tabular-nums' }}>
        {/* back link */}
        <button
          type="button"
          onClick={() => setScreen('organisations')}
          className="text-storm hover:text-code"
          style={{ background: 'transparent', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, padding: 0, marginBottom: 18, font: 'inherit', transition: 'color .12s' }}
        >
          <ArrowLeft size={15} /> All organisations
        </button>

        {/* header */}
        <div className="animate-fadeUp" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 10, flexWrap: 'wrap' }}>
              {sector && <span className="text-green-text uppercase tracking-eyebrow" style={{ fontSize: 11, fontWeight: 700 }}>{sector}</span>}
              <span className="bg-white border border-panel" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: st.ink, padding: '4px 10px', borderRadius: 999 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.dot }} />
                {st.label}
              </span>
            </div>
            <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.03em', margin: 0 }}>{name}</h1>
            <div className="text-storm" style={{ fontSize: 14, marginTop: 7 }}>{subLine}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Button variant="ghost" onClick={() => window.print()}>Print report</Button>
            <Button variant="ghost" onClick={() => exportAnalyticsCsv(a, name)}>
              <Download size={16} /> Export summary
            </Button>
            <Button onClick={() => openCampaignModal('edit', {
              campaignId: selectedOrg ?? undefined, name, sector: sector ?? '',
              status: (statusKey as 'active' | 'closed' | 'draft' | 'archived'),
              periodStart: campaign?.periodStart ?? '', periodEnd: campaign?.periodEnd ?? '',
              budgetUsd: budgetUsd != null ? String(budgetUsd) : '',
            })}>
              <Pencil size={15} /> Edit
            </Button>
          </div>
        </div>

        {/* governance bar */}
        <div className="animate-fadeUp bg-white border border-panel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', borderRadius: 8, padding: '11px 16px', marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShieldCheck size={17} color={identified ? '#C2A500' : '#00D700'} style={{ flex: 'none' }} />
            <span className="text-storm" style={{ fontSize: 13 }}>
              {identified
                ? 'Identified view: participant names and completion status are shown to programme admins for follow-up. Raw scores and interview transcripts are never shown. Access is audit-logged.'
                : 'Anonymised, role-level insights only. Teams under 5 participants are suppressed; individual responses and raw scores are never shown.'}
            </span>
          </div>
          <span className="text-storm border border-panel" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11.5, fontWeight: 600, borderRadius: 999, padding: '5px 12px', flex: 'none' }}>
            <Users size={14} className="text-green" /> Viewing as Versent admin
          </span>
        </div>

        {/* KPI row */}
        <div className="fui-kpi animate-fadeUp" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 14 }}>
          <Card pad={20}>
            <Eyebrow>Completion</Eyebrow>
            <Big><Count end={a.kpis.completionPct} /><span className="text-storm" style={{ fontSize: 22 }}>%</span></Big>
            <Bar pct={a.kpis.completionPct} />
            <div className="text-storm" style={{ fontSize: 12, marginTop: 8 }}>{a.kpis.completeCount} of {a.kpis.participantCount} complete</div>
          </Card>
          <Card pad={20}>
            <Eyebrow>Avg readiness</Eyebrow>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '10px 0 12px' }}>
              <span style={{ width: 13, height: 13, borderRadius: 4, background: bandByKey(a.kpis.avgReadiness.band).fill }} />
              <span style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em' }}>{a.kpis.avgReadiness.label}</span>
            </div>
            <div className="text-storm" style={{ fontSize: 12 }}>Cohort median band</div>
          </Card>
          <Card pad={20}>
            <Eyebrow>Total spend</Eyebrow>
            <Big><Money usd={b.spentUsd} /></Big>
            <div className="text-storm" style={{ fontSize: 12 }}>{audStr(avgUsd)} / completed assessment</div>
          </Card>
          <Card pad={20}>
            <Eyebrow>Budget used</Eyebrow>
            <Big><Count end={Math.round(budgetPct)} /><span className="text-storm" style={{ fontSize: 22 }}>%</span></Big>
            <Bar pct={budgetPct} />
            <div className="text-storm" style={{ fontSize: 12, marginTop: 8 }}>
              {budgetUsd != null ? `${audStr(b.spentUsd)} of ${audStr(budgetUsd)} allocated` : 'No budget set'}
            </div>
          </Card>
        </div>

        {/* radar + 4D distribution */}
        <div className="fui-2col animate-fadeUp" style={{ display: 'grid', gridTemplateColumns: '1fr 1.25fr', gap: 14, marginBottom: 14 }}>
          <Card pad={24}>
            <Eyebrow>Aggregate capability</Eyebrow>
            <div className="text-storm" style={{ fontSize: 13, margin: '4px 0 8px' }}>Cohort average across the five criteria</div>
            <div style={{ width: '100%', maxWidth: 300, margin: '0 auto' }}>
              <Radar axes={radarAxes} compact accent="#00D700" />
            </div>
            <table style={{ width: '100%', fontSize: 13, marginTop: 8 }}>
              <tbody>
                {a.capabilityRadar.map((c) => (
                  <tr key={c.criterion}>
                    <td className="text-storm" style={{ padding: '6px 0' }}>{CRITERION_LABELS[c.criterion]}</td>
                    <td style={{ padding: '6px 0', textAlign: 'right' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: bandByKey(c.band.band).fill }} />
                        {c.band.label}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <Card pad={24}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
              <div>
                <Eyebrow>4D readiness distribution</Eyebrow>
                <div className="text-storm" style={{ fontSize: 13, marginTop: 3 }}>Share of cohort at each level</div>
              </div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <Legend c="#00D700" l="Strong" /><Legend c="#2438E6" l="In place" /><Legend c="#3a3a44" l="Emerging" />
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {a.fourDDistribution.map((d) => {
                const total = d.strong + d.in_place + d.emerging || 1;
                const strongPct = Math.round((d.strong / total) * 100);
                return (
                  <div key={d.dimension}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 600, textTransform: 'capitalize' }}>{d.dimension}</span>
                      <span className="text-storm" style={{ fontSize: 12.5 }}>{strongPct}% strong</span>
                    </div>
                    <div className="bg-divider" style={{ display: 'flex', height: 12, borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{ width: `${(d.strong / total) * 100}%`, background: '#00D700' }} />
                      <div style={{ width: `${(d.in_place / total) * 100}%`, background: '#2438E6' }} />
                      <div style={{ width: `${(d.emerging / total) * 100}%`, background: '#3a3a44' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* org-custom dimension cohort (analytics) — only when the org has a current set with
            at least k completed assessments scored against it */}
        {a.customDimensionCohort && a.customDimensionCohort.dimensions.length > 0 && (
          <div className="fui-2col animate-fadeUp" style={{ marginBottom: 14 }}>
            <Card pad={24}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
                <Eyebrow>Organisation dimensions</Eyebrow>
                <span className="text-storm" style={{ fontSize: 12 }}>v{a.customDimensionCohort.version} · {a.customDimensionCohort.cohortSize} assessments</span>
              </div>
              <div className="text-storm" style={{ fontSize: 13, margin: '4px 0 8px' }}>Cohort average on your organisation&rsquo;s own dimensions</div>
              <table style={{ width: '100%', fontSize: 13 }}>
                <tbody>
                  {a.customDimensionCohort.dimensions.map((d) => (
                    <tr key={d.key}>
                      <td className="text-storm" style={{ padding: '6px 0' }}>
                        {d.label}
                        {d.informationalOnly && <span className="text-storm" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', marginLeft: 8, background: '#F0F0F2', padding: '2px 6px', borderRadius: 999 }}>Info</span>}
                      </td>
                      <td style={{ padding: '6px 0', textAlign: 'right' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: bandByKey(d.band.band).fill }} />
                          {d.band.label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        )}

        {/* org-custom dimensions authoring (admin) */}
        <div className="animate-fadeUp">
          <CustomDimensionsEditor
            campaignId={selectedOrg ?? a.campaignId}
            initial={campaign?.customDimensions}
            onPublished={(set) => setCampaign((prev) => (prev ? { ...prev, customDimensions: set } : { campaignId: a.campaignId, name, status: statusKey, customDimensions: set }))}
          />
        </div>

        {/* bottlenecks + teams */}
        <div className="fui-2col animate-fadeUp" style={{ display: 'grid', gridTemplateColumns: '1.25fr 1fr', gap: 14, marginBottom: 14 }}>
          <Card pad={24}>
            <Eyebrow>Top bottlenecks across the org</Eyebrow>
            <div className="text-storm" style={{ fontSize: 13, margin: '4px 0 18px' }}>Most common leverage leaks in completed assessments</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
              {a.topBottlenecks.map((t) => (
                <div key={t.key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 500 }}>{t.label}</span>
                    <span className="text-green-text" style={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}><Count end={t.pct} suffix="%" /></span>
                  </div>
                  <Bar pct={t.pct} h={8} />
                </div>
              ))}
            </div>
          </Card>
          <Card pad={24}>
            <Eyebrow>Readiness by team</Eyebrow>
            <div className="text-storm" style={{ fontSize: 13, margin: '4px 0 18px' }}>Average band, role-level only</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {a.readinessByTeam.map((t) => {
                const hidden = t.suppressed && (live || suppressed);
                const teamName = hidden && !live ? `${t.team} (suppressed, <5)` : t.team;
                return (
                  <div key={t.team} className="border-b border-divider" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '13px 0' }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{teamName}</div>
                      <div className="text-storm" style={{ fontSize: 12, marginTop: 2 }}>{t.participantCount} complete</div>
                    </div>
                    {!hidden && <BandChip band={t.band.band} label={t.band.label} />}
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* policy-risk summary */}
        <Card pad={24} className="animate-fadeUp" style={{ marginBottom: 14 }}>
          <div style={{ marginBottom: 18 }}><Eyebrow>Policy-risk summary</Eyebrow></div>
          <div className="fui-3col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
            <PolicyStat n={a.policyRisk.compliant} label="Compliant" color="#00A800" border="#C8E6C8" bg="rgba(0,215,0,0.06)" />
            <PolicyStat n={a.policyRisk.at_risk} label="At-risk" color="#C2A500" border="#EADFA8" bg="rgba(194,165,0,0.08)" />
            <PolicyStat n={a.policyRisk.non_compliant} label="Non-compliant" color="#E5142B" border="#F2C9CE" bg="rgba(229,20,43,0.05)" />
          </div>
          <div className="text-storm" style={{ fontSize: 12.5, marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertCircle size={14} className="text-storm" style={{ flex: 'none' }} />
            Counts of completed assessments by approved-tool fit. No individual is identifiable.
          </div>
        </Card>

        {/* roster */}
        <Card className="animate-fadeUp" style={{ overflow: 'hidden', padding: 0, marginBottom: 14 }}>
          <div style={{ padding: '20px 24px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <Eyebrow>Participant roster</Eyebrow>
              <div className="text-storm" style={{ fontSize: 13, marginTop: 3 }}>
                {identified ? 'Named participants · role and band · no raw scores' : suppressed ? `Small teams hidden · ${a.hiddenParticipantCount} participants in ${a.suppressedTeamCount} team suppressed` : 'Role-level identifiers · no names, no raw scores'}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={toggleSuppressed}
                title="Toggle k-anonymity suppression"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 600, padding: '7px 13px', borderRadius: 6, cursor: 'pointer', ...(suppressed ? { color: '#00A800', border: '1px solid #C8E6C8', background: 'rgba(0,215,0,0.07)' } : { color: '#75757F', border: '1px solid #E2E2E6', background: 'transparent' }) }}
              >
                {suppressed ? <EyeOff size={14} /> : <Eye size={14} />}
                {suppressed ? 'Small teams hidden' : 'Showing all teams'}
              </button>
              <button
                onClick={toggleIdentified}
                style={{ fontSize: 12.5, fontWeight: 600, padding: '7px 13px', borderRadius: 6, cursor: 'pointer', ...(identified ? { background: '#E0C200', color: '#000', border: '1px solid #E0C200' } : { border: '1px solid #E2E2E6', color: '#000', background: 'transparent' }) }}
              >
                {identified ? 'Hide names' : 'Identify participants'}
              </button>
            </div>
          </div>

          <div className="fui-rosterhead bg-faint text-storm" style={{ display: 'grid', gridTemplateColumns: ROSTER_COLS, gap: 0, borderTop: '1px solid #EFEFF2', borderBottom: '1px solid #EFEFF2', padding: '11px 24px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            <div>ID</div>
            <div>Name</div>
            <div>Role</div>
            <div>Team</div>
            <div>Status</div>
            <div>Band</div>
          </div>

          {a.roster.map((p) => (
            <div key={p.id} className="fui-rosterrow border-b border-divider" style={{ display: 'grid', gridTemplateColumns: ROSTER_COLS, gap: 0, padding: '14px 24px', alignItems: 'center' }}>
              <div className="text-storm" style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{p.id}</div>
              <div>
                {p.name ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar name={p.name} />
                    <div style={{ lineHeight: 1.25, minWidth: 0 }}>
                      <div className="text-code" style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                      {p.email && <div className="text-storm" style={{ fontSize: 11.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.email}</div>}
                    </div>
                  </div>
                ) : (
                  <span className="text-storm" style={{ fontSize: 13 }}>—</span>
                )}
              </div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{p.role}</div>
              <div className="text-storm" style={{ fontSize: 13.5 }}>{p.team}</div>
              <div><StatusDot status={p.status} /></div>
              <div>{p.band ? <BandChip band={p.band.band} label={p.band.label} /> : <span className="text-storm">—</span>}</div>
            </div>
          ))}

          {suppressed && (
            <div className="bg-faint" style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '14px 24px', borderTop: '1px solid #EFEFF2' }}>
              <EyeOff size={15} className="text-storm" style={{ flex: 'none' }} />
              <span className="text-storm" style={{ fontSize: 12.5 }}>{a.hiddenParticipantCount} participant(s) on teams under the 5-person threshold are hidden to protect anonymity.</span>
            </div>
          )}
        </Card>

        {/* cost & budget divider */}
        <div className="animate-fadeUp" style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '26px 0 14px' }}>
          <span className="text-green-text uppercase tracking-eyebrow" style={{ fontSize: 11, fontWeight: 700 }}>Cost &amp; budget</span>
          <span className="bg-panel" style={{ flex: 1, height: 1 }} />
        </div>

        {/* breakdown + burn */}
        <div className="fui-2col animate-fadeUp" style={{ display: 'grid', gridTemplateColumns: '1.25fr 1fr', gap: 14, marginBottom: 14 }}>
          <Card pad={24}>
            <Eyebrow>Where the spend goes</Eyebrow>
            <div className="text-storm" style={{ fontSize: 13, margin: '4px 0 18px' }}>Share of total cost by service</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
              {b.breakdown.map((c, i) => {
                const dot = BREAKDOWN_COLOR[c.key] ?? BREAKDOWN_PALETTE[i % BREAKDOWN_PALETTE.length];
                return (
                  <div key={c.key}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 7 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 9, height: 9, borderRadius: 2, background: dot }} />
                        {c.label}
                      </span>
                      <span className="text-storm" style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
                        {audStr(c.amountUsd)} · <span className="text-code" style={{ fontWeight: 700 }}>{c.pct}%</span>
                      </span>
                    </div>
                    <div className="bg-divider" style={{ height: 8, borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${c.pct}%`, background: dot, borderRadius: 999 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
          <Card pad={24} style={{ display: 'flex', flexDirection: 'column' }}>
            <Eyebrow>Budget burn</Eyebrow>
            <div className="text-storm" style={{ fontSize: 13, margin: '4px 0 20px' }}>
              {budgetUsd != null ? `Allocated ${audStr(budgetUsd)} for the programme` : 'No budget allocated'}
            </div>
            <div className="bg-divider" style={{ display: 'flex', height: 16, borderRadius: 999, overflow: 'hidden', marginBottom: 14 }}>
              <div style={{ width: `${spentPctOfBudget}%`, background: '#00D700' }} />
              <div style={{ width: `${projectedPctOfBudget}%`, background: 'repeating-linear-gradient(45deg,rgba(0,215,0,0.45),rgba(0,215,0,0.45) 5px,rgba(0,215,0,0.18) 5px,rgba(0,215,0,0.18) 10px)' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginTop: 'auto' }}>
              <LegendRow dot="#00D700" label="Spent so far" value={audStr(b.spentUsd)} />
              <LegendRow dot="rgba(0,215,0,0.4)" label="Projected remaining" value={audStr(b.projectedRemainingUsd)} />
              <div className="border-t border-divider" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 11 }}>
                <span style={{ fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: '#3a3a44' }} />
                  Headroom
                </span>
                <span style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: b.headroomUsd != null && b.headroomUsd < 0 ? '#E5142B' : '#00A800' }}>
                  {b.headroomUsd != null ? audStr(b.headroomUsd) : '—'}
                </span>
              </div>
            </div>
          </Card>
        </div>

        {/* cost per assessment */}
        <Card className="animate-fadeUp" style={{ overflow: 'hidden', padding: 0 }}>
          <div style={{ padding: '20px 24px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <Eyebrow>Cost per assessment</Eyebrow>
              <div className="text-storm" style={{ fontSize: 13, marginTop: 3 }}>Representative sample · role-level identifiers only</div>
            </div>
            <span className="text-storm border border-panel" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11.5, fontWeight: 600, borderRadius: 999, padding: '5px 12px' }}>Sorted by cost</span>
          </div>

          <div className="fui-rosterhead bg-faint text-storm" style={{ display: 'grid', gridTemplateColumns: COST_COLS, gap: 0, borderTop: '1px solid #EFEFF2', borderBottom: '1px solid #EFEFF2', padding: '11px 24px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            <div>ID</div><div>Role</div><div>Team</div><div>Duration</div><div>Tokens</div><div style={{ textAlign: 'right' }}>Cost</div>
          </div>

          {b.rows.map((r) => {
            const notStarted = r.costUsd == null;
            return (
              <div key={r.id} className="fui-rosterrow border-b border-divider" style={{ display: 'grid', gridTemplateColumns: COST_COLS, gap: 0, padding: '14px 24px', alignItems: 'center' }}>
                <div className="text-storm" style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{r.id}</div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{r.role}</div>
                <div className="text-storm" style={{ fontSize: 13.5 }}>{r.team}</div>
                <div className="text-storm" style={{ fontSize: 13.5, fontVariantNumeric: 'tabular-nums' }}>{r.durationMins != null ? `${r.durationMins} min` : '—'}</div>
                <div className="text-storm" style={{ fontSize: 13.5, fontVariantNumeric: 'tabular-nums' }}>{r.tokens != null ? `${(r.tokens / 1000).toFixed(1)}k` : '—'}</div>
                <div style={{ fontSize: 14, fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: notStarted ? '#9A9AA4' : '#000' }}>{notStarted ? '—' : audStr(r.costUsd as number)}</div>
              </div>
            );
          })}

          <div className="bg-faint" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px 24px', borderTop: '1px solid #EFEFF2' }}>
            <span className="text-storm" style={{ fontSize: 12.5 }}>Showing {b.rowsShown} of {b.rowsStarted} started · in-progress assessments billed for tokens used so far</span>
            <span className="text-storm" style={{ fontSize: 13 }}>Sample subtotal <span className="text-code" style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', marginLeft: 6 }}>{audStr(b.sampleSubtotalUsd)}</span></span>
          </div>
        </Card>
      </div>
    </div>
  );
}

const ROSTER_COLS = '88px 1.7fr 1.6fr 1.1fr 1fr 0.9fr';
const COST_COLS = '110px 1.5fr 1fr 0.85fr 0.85fr 0.8fr';

function Card({ children, pad, className = '', style }: { children: ReactNode; pad?: number; className?: string; style?: React.CSSProperties }) {
  return (
    <div className={`bg-white border border-panel rounded-card ${className}`} style={{ ...(pad != null ? { padding: pad } : {}), ...style }}>
      {children}
    </div>
  );
}

// Roster avatar: initials on a soft tint, deterministic per name so a person keeps the same colour.
const AVATAR_TINTS = [
  { bg: 'rgba(0,215,0,0.14)', ink: '#00850A' },
  { bg: 'rgba(36,56,230,0.12)', ink: '#2438E6' },
  { bg: 'rgba(110,113,128,0.16)', ink: '#53535C' },
  { bg: 'rgba(194,165,0,0.16)', ink: '#8A7400' },
];
function Avatar({ name }: { name: string }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
  const hash = [...name].reduce((h, c) => h + c.charCodeAt(0), 0);
  const t = AVATAR_TINTS[hash % AVATAR_TINTS.length];
  return (
    <span style={{ flex: 'none', width: 30, height: 30, borderRadius: '50%', background: t.bg, color: t.ink, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.01em' }}>
      {initials}
    </span>
  );
}

function Big({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 40, fontWeight: 800, letterSpacing: '-0.03em', margin: '6px 0 10px', fontVariantNumeric: 'tabular-nums' }}>{children}</div>;
}

function Bar({ pct, h = 6 }: { pct: number; h?: number }) {
  return (
    <div className="bg-divider" style={{ height: h, borderRadius: 999, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${Math.min(100, pct)}%`, background: '#00D700', borderRadius: 999 }} />
    </div>
  );
}

function Money({ usd }: { usd: number }) {
  return (
    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
      <span className="text-storm" style={{ fontSize: 22 }}>A$</span>
      <Count end={aud(usd)} decimals={2} />
    </span>
  );
}

function Legend({ c, l }: { c: string; l: string }) {
  return (
    <span className="text-storm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5 }}>
      <span style={{ width: 9, height: 9, borderRadius: 2, background: c }} />
      {l}
    </span>
  );
}

function LegendRow({ dot, label, value }: { dot: string; label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span className="text-storm" style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 9, height: 9, borderRadius: 2, background: dot }} />
        {label}
      </span>
      <span style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

function PolicyStat({ n, label, color, border, bg }: { n: number; label: string; color: string; border: string; bg: string }) {
  return (
    <div style={{ border: `1px solid ${border}`, background: bg, borderRadius: 8, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 32, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums' }}><Count end={n} /></span>
        <span className="text-storm" style={{ fontSize: 13 }}>people</span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, marginTop: 6 }}>{label}</div>
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  const map: Record<string, { dot: string; ink: string; label: string }> = {
    completed: { dot: '#00D700', ink: '#00A800', label: 'Complete' },
    in_progress: { dot: '#C2A500', ink: '#A98300', label: 'In progress' },
    created: { dot: '#9A9AA4', ink: '#75757F', label: 'Not started' },
  };
  const s = map[status] ?? map.created;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: s.ink }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.dot }} />
      {s.label}
    </span>
  );
}

function Message({ title, tone = 'info', onBack }: { title: string; tone?: 'info' | 'error'; onBack: () => void }) {
  return (
    <div className="bg-subtle text-code" style={{ flex: 1 }}>
      <div className="fui-admin" style={{ maxWidth: 1180, margin: '0 auto', padding: '34px 32px 80px' }}>
        <button type="button" onClick={onBack} className="text-storm hover:text-code" style={{ background: 'transparent', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, padding: 0, marginBottom: 18, font: 'inherit' }}>
          <ArrowLeft size={15} /> All organisations
        </button>
        <div className={`bg-white rounded-card border ${tone === 'error' ? 'border-critical' : 'border-panel'}`} style={{ padding: 24, fontSize: 14, color: tone === 'error' ? '#E5142B' : '#75757F' }}>
          {title}
        </div>
      </div>
    </div>
  );
}
